<template>
    <div class="minute-wrap">
        <div class="minute-toolbar">
            <button
                type="button"
                class="btn btn-outline-primary btn-sm me-1"
                :disabled="pdfPrinting"
                @click="handlePdfPrint"
            >
                <i class="fa fa-print"></i>
            </button>
            <button
                type="button"
                class="btn btn-outline-primary btn-sm"
                :disabled="emailSending"
                :title="emailSending ? 'Sending email...' : 'Send Meeting Minute by Email'"
                @click="handleSendEmail"
            >
                <i class="fa fa-envelope"></i>
            </button>
        </div>
        <div class="minute-table-card">
          <div class="minute-table-scroll">
            <table class="minute-table">
                <thead>
                    <tr>
                        <th class="col-sl">SL No</th>
                        <th class="col-note">Note</th>
                        <th class="col-action">Task / Event ?</th>
                        <th class="col-team">Team</th>
                        <th class="col-schedule">Schedule</th>
                        <th class="col-client">Client</th>
                        <th class="col-detail">Other Detail</th>
                        <th class="col-status">Status</th>
                    </tr>
                </thead>

                <tbody>
                    <template v-if="minutes.length">
                        <tr
                            v-for="(item, index) in minutes"
                            :key="minuteKey(item, index)"
                            class="minute-row"
                            :class="{ 'minute-row--saved': item.id, 'minute-row--draft': ! item.id }"
                        >
                            <td class="col-sl">{{ index + 1 }}</td>
                            <td class="col-note">
                                <textarea
                                    v-model="item.note"
                                    class="minute-note-input"
                                    rows="1"
                                    placeholder="Type a note..."
                                    @blur="saveMinute(item)"
                                    @input="autoGrow($event)"
                                ></textarea>
                            </td>
                            <td class="col-action">
                                <button
                                    type="button"
                                    class="minute-action-btn"
                                    :class="{ 'minute-action-btn-linked': item.linked_activity_log_id }"
                                    :disabled="! item.id"
                                    @click="handleSchedulerOpen(item)"
                                >
                                    <i class="fa fa-link" v-if="item.linked_activity_log_id"></i>
                                    <i class="fa fa-plus" v-if="! item.linked_activity_log_id"></i>
                                    {{ minuteActionLabel(item) }}
                                </button>
                            </td>
                            <td class="col-team">{{ item.team }}</td>
                            <td class="col-schedule">{{ item.schedule }}</td>
                            <td class="col-client">{{ item.client }}</td>
                            <td class="col-detail">{{ item.other_detail }}</td>
                            <td class="col-status">
                                <span v-if="item.status" class="minute-status-pill" :style="statusStyle(item.status)">
                                    {{ item.status }}
                                </span>
                            </td>
                        </tr>
                    </template>
                    <tr v-else class="minute-empty-row">
                        <td colspan="8">
                            <div class="minute-empty-state">
                                <span class="minute-empty-state__icon" aria-hidden="true">
                                    <i class="fa fa-clipboard"></i>
                                </span>
                                <div class="minute-empty-state__text">
                                    <strong>No minutes yet</strong>
                                    <span>Start typing in the note field to add your first meeting minute</span>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
          </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick, inject, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { getTaskStatusColor } from '@/services/utils/global.js';
import handleActivity from '@/services/modules/procurement/activity';
import handleBarcode from '@/services/modules/barcode';
import handleCompany from '@/services/modules/company';
import MeetingMinutePdfPrinter from '@/services/utils/MeetingMinutePdfPrinter';

const props = defineProps({
    parentActivityLogId: {
        type: Number,
        default: null
    },
    parentType: {
        type: String,
        default: 'task'
    },
    reloadTrigger: {
        type: Number,
        default: 0
    }
});

const emit = defineEmits(['openScheduler']);

const route = useRoute();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const store = useStore();

const {
    fetchActivityMinutes,
    fetchSingleTaskActivityLog,
    storeActivityMinute,
    updateActivityMinute,
    sendMeetingMinuteEmail
} = handleActivity();

const { fetchBarcode } = handleBarcode();

const minutes = ref([]);
const savingId = ref(null);
const latestRequestId = ref(0);
const parentDetails = ref(null);
const parentLatestRequestId = ref(0);
const pdfPrinting = ref(false);
const emailSending = ref(false);
const { exportMeetingMinutePdf } = MeetingMinutePdfPrinter();
const { fetchCompanyInfo } = handleCompany();
const companyInfoRef = ref({});
const isMounted = ref(false);
const localKeyCounter = ref(0);

const getCompanyInfo = async () => {
    if (companyInfoRef.value && companyInfoRef.value.id) {
        return companyInfoRef.value;
    }
    const fetched = await fetchCompanyInfo(route.params.companyId);
    if (fetched && fetched.data) {
        companyInfoRef.value = fetched.data;
    }
    return companyInfoRef.value;
};

const appendEmptyRow = () => {
    localKeyCounter.value = localKeyCounter.value + 1;
    minutes.value.push({
        id: null,
        note: '',
        saved_note: '',
        linked_activity_log_id: null,
        linked_type: null,
        team: null,
        schedule: null,
        client: null,
        other_detail: null,
        status: null,
        _localKey: 'draft-' + localKeyCounter.value
    });
};

const hasTrailingEmptyRow = () => {
    if (! minutes.value.length) {
        return false;
    }
    const last = minutes.value[minutes.value.length - 1];
    if (last.id) {
        return false;
    }
    if (last.note) {
        return false;
    }
    return true;
};

const captureUnsavedDraft = () => {
    if (! minutes.value.length) {
        return null;
    }
    const last = minutes.value[minutes.value.length - 1];
    if (last.id) {
        return null;
    }
    if (! last.note) {
        return null;
    }
    return { note: last.note, _localKey: last._localKey };
};

const restoreUnsavedDraft = (draft) => {
    if (! draft) {
        return;
    }
    if (! hasTrailingEmptyRow()) {
        return;
    }
    const last = minutes.value[minutes.value.length - 1];
    last.note = draft.note;
    last._localKey = draft._localKey;
};

const loadMinutes = () => {
    if (! props.parentActivityLogId) {
        return;
    }
    latestRequestId.value = latestRequestId.value + 1;
    const requestId = latestRequestId.value;
    const query = '?company_id=' + route.params.companyId;
    const preservedDraft = captureUnsavedDraft();
    fetchActivityMinutes(props.parentActivityLogId, query).then(res => {
        if (! isMounted.value) {
            return;
        }
        if (requestId !== latestRequestId.value) {
            return;
        }
        if (! res.status) {
            showError(res.message);
            return;
        }
        minutes.value = res.data.map(row => {
            return { ...row, saved_note: row.note, _localKey: 'minute-' + row.id };
        });
        if (! hasTrailingEmptyRow()) {
            appendEmptyRow();
        }
        restoreUnsavedDraft(preservedDraft);
    });
};

const minuteActionLabel = (item) => {
    if (! item.linked_activity_log_id) {
        return 'Create';
    }
    return 'Edit';
};

const saveMinute = (item) => {
    if (! item.note) {
        return;
    }
    if (! item.note.trim()) {
        return;
    }
    if (item.note === item.saved_note) {
        return;
    }
    if (savingId.value === item) {
        return;
    }

    savingId.value = item;

    const payload = {
        company_id: route.params.companyId,
        activity_log_id: props.parentActivityLogId,
        note: item.note
    };

    if (! item.id) {
        storeActivityMinute(payload)
            .then(res => {
                if (! isMounted.value) {
                    return;
                }
                if (! res.status) {
                    showError(res.message);
                    return;
                }
                item.id = res.data.id;
                item.saved_note = item.note;
                if (! hasTrailingEmptyRow()) {
                    nextTick(() => {
                        appendEmptyRow();
                    });
                }
            })
            .catch(() => {
                if (! isMounted.value) {
                    return;
                }
                showError('Something went wrong');
            })
            .finally(() => {
                savingId.value = null;
            });
        return;
    }

    updateActivityMinute(payload, item.id)
        .then(res => {
            if (! isMounted.value) {
                return;
            }
            if (! res.status) {
                showError(res.message);
                return;
            }
            item.saved_note = item.note;
        })
        .catch(() => {
            if (! isMounted.value) {
                return;
            }
            showError('Something went wrong');
        })
        .finally(() => {
            savingId.value = null;
        });
};

const handleSchedulerOpen = (item) => {
    if (! item.id) {
        showError('Please save the note first');
        return;
    }
    emit('openScheduler', {
        minute_id: item.id,
        note: item.note,
        linked_activity_log_id: item.linked_activity_log_id,
        parent_activity_log_id: props.parentActivityLogId
    });
};

const statusStyle = (status) => {
    const color = getTaskStatusColor(status);
    return {
        backgroundColor: color.bg,
        color: color.text,
        border: '1px solid ' + color.border
    };
};

const autoGrow = (event) => {
    const el = event.target;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
};

const minuteKey = (item, index) => {
    if (item._localKey) {
        return item._localKey;
    }
    return 'minute-fallback-' + index;
};

const reloadMinutes = () => {
    loadMinutes();
};

const loadParentDetails = async () => {
    if (! props.parentActivityLogId) {
        parentDetails.value = null;
        return null;
    }
    parentLatestRequestId.value = parentLatestRequestId.value + 1;
    const requestId = parentLatestRequestId.value;
    const query = '?company_id=' + route.params.companyId;
    try {
        const res = await fetchSingleTaskActivityLog(props.parentActivityLogId, query);
        if (! isMounted.value) {
            return parentDetails.value;
        }
        if (requestId !== parentLatestRequestId.value) {
            return parentDetails.value;
        }
        if (! res || ! res.status) {
            parentDetails.value = null;
            return null;
        }
        parentDetails.value = res.data || null;
        return parentDetails.value;
    } catch (e) {
        if (requestId !== parentLatestRequestId.value) {
            return parentDetails.value;
        }
        parentDetails.value = null;
        return null;
    }
};

const handlePdfPrint = async () => {
    if (pdfPrinting.value) {
        return;
    }
    const userName = store.state.user.user.name;
    pdfPrinting.value = true;
    try {
        const printableMinutes = (minutes.value || []).filter((minute) => {
            return minute && minute.id;
        });
        if (! printableMinutes.length) {
            showError('No meeting minute data available to print.');
            return;
        }

        const parent = await loadParentDetails();
        if (! parent) {
            showError('No meeting minute data available to print.');
            return;
        }
        const qrcodeData = JSON.stringify({
            ref_no: parent.short_description || parent.id || 'meeting-minute',
            ref_date: parent.created_at || new Date().toISOString(),
            created_by: userName,
            created_date_time: new Date().toISOString()
        });
        const code = parent.id ? String(parent.id) : (parent.short_description || 'MEETING_MINUTE');
        const barcodeQuery = `?code=${encodeURIComponent(code)}&qrcode=${encodeURIComponent(qrcodeData)}`;
        const barcodeRes = await fetchBarcode(barcodeQuery);
        if (! barcodeRes || ! barcodeRes.status) {
            showError('Something went wrong');
            return;
        }

        const company = await getCompanyInfo();
        if (! company || ! company.id) {
            showError('Something went wrong');
            return;
        }
        exportMeetingMinutePdf(company, parent, printableMinutes, userName, barcodeRes.barcode, barcodeRes.qrcode);
    } catch (e) {
        showError('Something went wrong');
    } finally {
        pdfPrinting.value = false;
    }
};

const handleSendEmail = async () => {
    if (emailSending.value) {
        return;
    }
    emailSending.value = true;
    try {
        const printableMinutes = (minutes.value || []).filter((minute) => {
            return minute && minute.id;
        });
        if (! printableMinutes.length) {
            showError('No meeting minute data available to send.');
            return;
        }

        const payload = { company_id: route.params.companyId };
        const res = await sendMeetingMinuteEmail(payload, props.parentActivityLogId);
        if (! res || ! res.status) {
            showError((res && res.message) ? res.message : 'Failed to send meeting minute email');
            return;
        }
        showSuccess(res.message);
    } catch (e) {
        showError('Something went wrong');
    } finally {
        emailSending.value = false;
    }
};

onMounted(() => {
    isMounted.value = true;
    loadMinutes();
    loadParentDetails();
    getCompanyInfo();
    nextTick(() => {
        document.querySelectorAll('.minute-note-input').forEach(el => {
            el.style.height = el.scrollHeight + 'px';
        });
    });
});

onBeforeUnmount(() => {
    isMounted.value = false;
});

watch(() => props.reloadTrigger, (value, oldValue) => {
    if (value === oldValue) {
        return;
    }
    loadMinutes();
});

watch(() => props.parentActivityLogId, (value, oldValue) => {
    if (value === oldValue) {
        return;
    }
    parentDetails.value = null;
    loadMinutes();
    loadParentDetails();
});

defineExpose({ loadMinutes, reloadMinutes });
</script>

<style scoped>
.minute-wrap {
    padding: 4px 0 0;
}
.minute-toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;
}
.minute-table-card {
    border: 1px solid #e6e9ef;
    border-radius: 12px;
    overflow: hidden;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.minute-table-scroll {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}
.minute-table-scroll .minute-table {
    min-width: 760px;
}
.minute-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    font-size: 0.86rem;
    word-break: break-word;
}
.minute-table thead th {
    background: #f5f7fb;
    color: #475467;
    font-weight: 600;
    text-align: left;
    padding: 11px 12px;
    border-bottom: 1px solid #e6e9ef;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
}
.minute-row td {
    padding: 12px;
    border-bottom: 1px solid #f2f4f7;
    vertical-align: top;
    transition: background 160ms ease;
}
.minute-row:nth-child(even) td {
    background: #fbfcfe;
}
.minute-row:hover td {
    background: #f5f6ff;
}
.minute-row--draft td {
    background: #fbfcfe;
}
.minute-row:last-of-type td {
    border-bottom: none;
}
.col-sl {
    width: 44px;
    text-align: center;
    color: #98a2b3;
    font-weight: 600;
}
.col-note {
    width: 18%;
}
.col-action {
    width: 92px;
}
.col-team {
    width: 18%;
}
.col-schedule {
    width: 14%;
}
.col-client {
    width: 10%;
}
.col-detail {
    width: 16%;
}
.col-status {
    width: 92px;
}
.col-team, .col-schedule, .col-client, .col-detail {
    word-break: break-word;
    overflow-wrap: anywhere;
    color: #475467;
    white-space: normal;
}
.minute-note-input {
    display: block;
    width: 100%;
    box-sizing: border-box;
    border: 1px solid #e4e7ec;
    background: #ffffff;
    resize: none;
    padding: 8px 10px;
    border-radius: 8px;
    font-size: 0.86rem;
    font-family: inherit;
    color: #1f2a44;
    line-height: 1.45;
    overflow: hidden;
    min-height: 36px;
    transition: border-color 160ms ease, box-shadow 160ms ease;
}
.minute-note-input::placeholder {
    color: #98a2b3;
}
.minute-note-input:hover {
    border-color: #c7d2fe;
}
.minute-note-input:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14);
}
.minute-action-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid #c7d2fe;
    background: #ffffff;
    color: #6366f1;
    padding: 5px 12px;
    border-radius: 999px;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 160ms ease, color 160ms ease, border-color 160ms ease, transform 160ms ease;
}
.minute-action-btn:hover:not(:disabled) {
    background: #eef0ff;
    border-color: #6366f1;
}
.minute-action-btn:focus-visible {
    outline: 2px solid #6366f1;
    outline-offset: 2px;
}
.minute-action-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
.minute-action-btn-linked {
    background: #6366f1;
    border-color: #6366f1;
    color: #ffffff;
    box-shadow: 0 2px 6px -2px rgba(99, 102, 241, 0.45);
}
.minute-action-btn-linked:hover:not(:disabled) {
    background: #4f46e5;
    border-color: #4f46e5;
    color: #ffffff;
}
.minute-status-pill {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.minute-empty-row td {
    border-bottom: none;
    background: #ffffff;
}
.minute-empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 14px;
    padding: 32px 16px;
    color: #6b7691;
}
.minute-empty-state__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: #eef0ff;
    color: #6366f1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.minute-empty-state__text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
}
.minute-empty-state__text strong {
    color: #1f2a44;
    font-size: 0.9rem;
    font-weight: 600;
}
.minute-empty-state__text span {
    font-size: 0.78rem;
    color: #6b7691;
}
@media (max-width: 767.98px) {
    .minute-table {
        font-size: 0.82rem;
    }
    .minute-table thead th,
    .minute-row td {
        padding: 10px 8px;
    }
    .minute-empty-state {
        padding: 24px 12px;
        gap: 10px;
    }
    .minute-empty-state__icon {
        width: 38px;
        height: 38px;
        font-size: 16px;
    }
}
@media (max-width: 575.98px) {
    .minute-table {
        font-size: 0.78rem;
    }
    .minute-table thead th,
    .minute-row td {
        padding: 8px 6px;
    }
    .minute-action-btn {
        padding: 4px 8px;
        font-size: 0.7rem;
        gap: 4px;
    }
    .minute-note-input {
        padding: 6px 8px;
        font-size: 0.82rem;
        min-height: 32px;
    }
    .minute-status-pill {
        padding: 2px 8px;
        font-size: 10px;
    }
    .minute-empty-state {
        padding: 20px 10px;
        flex-direction: column;
        text-align: center;
    }
    .minute-empty-state__text {
        align-items: center;
    }
}
@media (max-width: 374.98px) {
    .minute-table thead th,
    .minute-row td {
        padding: 6px 4px;
    }
    .minute-action-btn span,
    .minute-action-btn i + span {
        font-size: 0.65rem;
    }
}
</style>
